// webUsbCapture.js — the ONE in-browser Android capture session, shared by the
// homepage toolbar button (AndroidCaptureButton) and the Device capture card
// (DeviceCapturePanel). Only one program can hold the phone's USB interface,
// so two components each owning a session would fight over it; and the
// session must outlive page navigation (it stops when the tab closes).

import { useSyncExternalStore } from 'react';
import { startWebUsbTap, explainError, abortPendingConnect } from './webAdbTap';
import { PROXY_API_BASE } from '../api/proxyAuth';

const API_BASE = PROXY_API_BASE || 'http://localhost:8001';
const NAME_KEY = 'fx_adb_device_name';

let state = { phase: 'idle', detail: '', stats: null, device: null };
let session = null;
let cancelled = false;
const listeners = new Set();

function set(patch) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export function getDeviceName() {
  try { return localStorage.getItem(NAME_KEY) || 'android-prod'; } catch { return 'android-prod'; }
}
export function setDeviceName(name) {
  try { localStorage.setItem(NAME_KEY, name); } catch { /* ignore */ }
}
const safeName = (n) => (String(n || '').trim() || 'android-prod').replace(/[^\w.-]/g, '-');

export const isActive = (s = state) => ['selecting', 'authorizing', 'running'].includes(s.phase);

// Must be called from a click handler (the browser's device picker needs a user gesture).
export async function startCapture(deviceName = getDeviceName()) {
  if (isActive()) return;
  set({ phase: 'selecting', detail: '', stats: null, device: null });
  cancelled = false;
  try {
    session = await startWebUsbTap({
      apiBase: API_BASE,
      deviceName: safeName(deviceName),
      onStatus: (phase, detail) => {
        set({ phase, detail: detail || '' });
        if (phase === 'stopped' || phase === 'error') session = null;
      },
      onStats: (stats) => set({ stats }),
    });
    set({ device: session.device });
  } catch (e) {
    session = null;
    // A user Cancel surfaces here as a closed-device error; it isn't one.
    set(cancelled ? { phase: 'idle', detail: '' } : { phase: 'error', detail: explainError(e) });
  }
}

export async function stopCapture() {
  if (session) await session.stop();
  else if (isActive()) {
    // Cancel while waiting on the picker/Allow prompt: release the phone too,
    // or the next attempt finds it held by this very tab.
    cancelled = true;
    await abortPendingConnect();
    set({ phase: 'idle', detail: '' });
  }
}

function subscribe(l) { listeners.add(l); return () => listeners.delete(l); }
const snapshot = () => state;

export function useWebUsbCapture() {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

// tests only
export function _reset() { state = { phase: 'idle', detail: '', stats: null, device: null }; session = null; cancelled = false; listeners.forEach((l) => l()); }
